import React from "react"
import FormActivityLog from "./FormActivityLog"

const FORM_LINK_CLASS = "text-blue-700 underline cursor-pointer"

const GOAL_PLANNING_LINKS = {
  sampleQuestions:
    "https://docs.google.com/document/d/1hfIa9y5pYiTu_CPa89KzdsBi84L52lpJarLgzdGxgvM/edit?usp=sharing",
  growthCriteria:
    "https://docs.google.com/document/d/1ys9BnEWA89YgqSYJ8yrcX28j_v4cQ9mIhW8W5JPvkr0/edit?usp=drive_link",
  appendixB:
    "https://docs.google.com/document/d/1JFSiRfnAeS9cBOi405Zr1y8BId7aoHUBrOT5TGFfQQI/edit?usp=sharing",
  appendixC:
    "https://docs.google.com/document/d/1ys9BnEWA89YgqSYJ8yrcX28j_v4cQ9mIhW8W5JPvkr0/edit?usp=drive_link",
}

const TEXTAREA_CLASS =
  "w-full min-h-[120px] resize-y border border-gray-300 rounded p-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#241763]/30 focus:border-[#241763]"

const TEXTAREA_READ_ONLY_CLASS =
  "w-full min-h-[120px] resize-y border border-gray-200 rounded p-2 text-sm bg-gray-50 text-gray-700"

const GoalPlanningLink = ({ href, children }) => (
  <a
    href={href}
    target="_blank"
    rel="noopener noreferrer"
    className={FORM_LINK_CLASS}
    aria-label={`${children} (opens in a new tab)`}
  >
    {children}
  </a>
)

export const EMPTY_GOAL_PLANNING_FORM = {
  pselOpportunities: "",
  leadershipQuestions: "",
  professionalExperiences: "",
  newLearning: "",
  goalsAndRationale: "",
  learningPlan: "",
  learningActionQuestions: "",
  evidenceOfLearning: "",
  multiYearFocus: "N/A",
  schoolDistrictContribution: "",
  conferenceNotes: "",
  tier2Support: false,
  conferenceDate: "",
}

export const mapGoalPlanningToForm = (goalPlanning) => ({
  pselOpportunities: goalPlanning?.pselOpportunities || "",
  leadershipQuestions: goalPlanning?.leadershipQuestions || "",
  professionalExperiences: goalPlanning?.professionalExperiences || "",
  newLearning: goalPlanning?.newLearning || "",
  goalsAndRationale: goalPlanning?.goalsAndRationale || "",
  learningPlan: goalPlanning?.learningPlan || "",
  learningActionQuestions: goalPlanning?.learningActionQuestions || "",
  evidenceOfLearning: goalPlanning?.evidenceOfLearning || "",
  multiYearFocus: goalPlanning?.multiYearFocus || "N/A",
  schoolDistrictContribution: goalPlanning?.schoolDistrictContribution || "",
  conferenceNotes: goalPlanning?.conferenceNotes || "",
  tier2Support: goalPlanning?.tier2Support || false,
  conferenceDate: goalPlanning?.conferenceDate || "",
})

const SectionHeader = ({ title, subtitle }) => (
  <tr>
    <td
      colSpan={2}
      className="border border-gray-400 bg-[#FDE9D9] text-center py-2"
    >
      <div className="font-bold text-sm">{title}</div>
      {subtitle && <div className="italic text-sm">{subtitle}</div>}
    </td>
  </tr>
)

const FormRow = ({ question, field, value, onFieldChange, readOnly = false }) => (
  <tr className="border border-gray-400">
    <td className="w-[38%] align-top border border-gray-400 p-3 text-sm bg-white">
      {question}
    </td>
    <td className="w-[62%] align-top border border-gray-400 p-2 bg-white">
      <textarea
        value={value || ""}
        onChange={(e) => onFieldChange(field, e.target.value)}
        readOnly={readOnly}
        rows={5}
        className={`${readOnly ? TEXTAREA_READ_ONLY_CLASS : TEXTAREA_CLASS} ${
          field === "multiYearFocus" ? "italic" : ""
        }`}
        aria-label={typeof question === "string" ? question : field}
      />
    </td>
  </tr>
)

const GoalPlanningForm = ({
  formData,
  onChange,
  mode = "admin",
  adminName,
  record = null,
}) => {
  const isLeaderReadOnly =
    mode === "super_admin_conference" || mode === "view" || mode === "initiate"
  const isConferenceReadOnly =
    mode === "admin" || mode === "initiate" || mode === "view"

  return (
    <div className="space-y-4">
      {adminName && (
        <p className="text-sm text-gray-600">
          Goal Planning for: <span className="font-semibold">{adminName}</span>
        </p>
      )}

      <div className="border border-gray-400 overflow-hidden">
        <div className="bg-[#ED7D31] text-center font-bold py-2 border-b border-gray-400 text-sm">
          Beginning of the Year Goals &amp; Planning
        </div>

        <table className="w-full border-collapse">
          <tbody>
            <SectionHeader
              title="Self-Reflection"
              subtitle="Completed by Leader"
            />

            <tr>
              <td
                colSpan={2}
                className="border border-gray-400 p-3 text-sm bg-white"
              >
                Summarize your self-reflection below; consider using the{" "}
                <GoalPlanningLink href={GOAL_PLANNING_LINKS.sampleQuestions}>
                  Sample Questions
                </GoalPlanningLink>{" "}
                found within the model to guide your thinking. Additionally,{" "}
                <GoalPlanningLink href={GOAL_PLANNING_LINKS.growthCriteria}>
                  Growth Criteria and Sources of Evidence
                </GoalPlanningLink>{" "}
                can be reviewed for purposes of reflection.
              </td>
            </tr>

            <FormRow
              question="In reviewing the PSEL standards, what areas emerge as opportunities for your professional learning and practice?"
              field="pselOpportunities"
              value={formData.pselOpportunities}
              onFieldChange={onChange}
              readOnly={isLeaderReadOnly}
            />
            <FormRow
              question="What question(s) do you have about leadership and organizational well-being?"
              field="leadershipQuestions"
              value={formData.leadershipQuestions}
              onFieldChange={onChange}
              readOnly={isLeaderReadOnly}
            />
            <FormRow
              question="What professional experiences/data/evidence do you have that led you to this question?"
              field="professionalExperiences"
              value={formData.professionalExperiences}
              onFieldChange={onChange}
              readOnly={isLeaderReadOnly}
            />
            <FormRow
              question="What new learning might you want to explore to inform your understanding of these questions and professional leadership practice?"
              field="newLearning"
              value={formData.newLearning}
              onFieldChange={onChange}
              readOnly={isLeaderReadOnly}
            />

            <SectionHeader
              title="Goal, Rationale, Alignment and Professional Learning Plan"
              subtitle="Completed by Leader"
            />

            <tr className="border border-gray-400">
              <td className="w-[38%] align-top border border-gray-400 p-3 text-sm bg-[#FDE9D9]">
                Based on your analysis above, what is/are your goal(s)? Include a
                rationale for the length of your goal (1, 2, 3 year).
              </td>
              <td className="w-[62%] align-top border border-gray-400 p-2 bg-white">
                <div className="text-sm font-medium mb-1 px-2">1 year goal</div>
                <textarea
                  value={formData.goalsAndRationale}
                  onChange={(e) =>
                    onChange("goalsAndRationale", e.target.value)
                  }
                  readOnly={isLeaderReadOnly}
                  rows={5}
                  className={
                    isLeaderReadOnly ? TEXTAREA_READ_ONLY_CLASS : TEXTAREA_CLASS
                  }
                  aria-label="Goals and rationale"
                />
              </td>
            </tr>

            <tr className="border border-gray-400">
              <td className="w-[38%] align-top border border-gray-400 p-3 text-sm bg-[#FDE9D9]">
                What is your learning plan to support achieving your goal?{" "}
                <GoalPlanningLink href={GOAL_PLANNING_LINKS.appendixB}>
                  See Appendix B: Professional Learning &amp; Action Questions
                  to Guide Your Plan
                </GoalPlanningLink>
              </td>
              <td className="w-[62%] align-top border border-gray-400 p-2 bg-white">
                <textarea
                  value={formData.learningPlan}
                  onChange={(e) => onChange("learningPlan", e.target.value)}
                  readOnly={isLeaderReadOnly}
                  rows={5}
                  className={
                    isLeaderReadOnly ? TEXTAREA_READ_ONLY_CLASS : TEXTAREA_CLASS
                  }
                  aria-label="Learning plan"
                />
              </td>
            </tr>

            <tr className="border border-gray-400">
              <td className="w-[38%] align-top border border-gray-400 p-3 text-sm bg-white">
                <GoalPlanningLink href={GOAL_PLANNING_LINKS.appendixB}>
                  Learning &amp; Action Questions to Guide Your Plan
                </GoalPlanningLink>
              </td>
              <td className="w-[62%] align-top border border-gray-400 p-2 bg-white">
                <textarea
                  value={formData.learningActionQuestions}
                  onChange={(e) =>
                    onChange("learningActionQuestions", e.target.value)
                  }
                  readOnly={isLeaderReadOnly}
                  rows={5}
                  className={
                    isLeaderReadOnly ? TEXTAREA_READ_ONLY_CLASS : TEXTAREA_CLASS
                  }
                  aria-label="Learning and action questions"
                />
              </td>
            </tr>

            <FormRow
              question={
                <>
                  What evidence of leader learning, educator learning, and/or
                  student growth and achievement, and/or organizational measures
                  will you use to reflect, monitor, and adjust your goal? See{" "}
                  <GoalPlanningLink href={GOAL_PLANNING_LINKS.appendixC}>
                    Appendix C Growth Criteria and Sources of Evidence
                  </GoalPlanningLink>
                </>
              }
              field="evidenceOfLearning"
              value={formData.evidenceOfLearning}
              onFieldChange={onChange}
              readOnly={isLeaderReadOnly}
            />

            <FormRow
              question="For multi-year goal(s), what might be the potential focus of years 2 and 3 (to be revisited and revised annually and as needed throughout the learning process)?"
              field="multiYearFocus"
              value={formData.multiYearFocus}
              onFieldChange={onChange}
              readOnly={isLeaderReadOnly}
            />

            <FormRow
              question="In what ways might this goal(s) contribute to the school and/or district's vision, mission, and strategic goals?"
              field="schoolDistrictContribution"
              value={formData.schoolDistrictContribution}
              onFieldChange={onChange}
              readOnly={isLeaderReadOnly}
            />
          </tbody>
        </table>

        <div className="bg-[#ED7D31] text-center py-2 border-t border-gray-400">
          <div className="font-bold text-sm">Goal Setting Conference</div>
          <div className="italic text-sm">Completed by Evaluator (By November 1)</div>
          <div className="italic text-sm">Date</div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 border-t border-gray-400">
          <div className="border border-gray-400 p-3 bg-white min-h-[200px]">
            <div className="font-bold text-sm mb-2">Notes:</div>
            <textarea
              value={formData.conferenceNotes}
              onChange={(e) => onChange("conferenceNotes", e.target.value)}
              readOnly={isConferenceReadOnly}
              rows={8}
              placeholder={
                isConferenceReadOnly
                  ? "To be completed by Super Admin after admin submission"
                  : ""
              }
              className={`w-full min-h-[160px] resize-y rounded p-2 text-sm border ${
                isConferenceReadOnly
                  ? "bg-gray-50 text-gray-500 border-gray-200"
                  : "bg-white border-gray-300 focus:outline-none focus:ring-2 focus:ring-[#241763]/30 focus:border-[#241763]"
              }`}
              aria-label="Goal setting conference notes"
            />
          </div>

          <div className="border border-gray-400 p-3 bg-white min-h-[200px] flex flex-col">
            <div className="font-bold text-sm mb-3">
              Supports Required/Suggested
            </div>
            <label className="flex items-center gap-2 text-sm mb-auto">
              <input
                type="checkbox"
                checked={formData.tier2Support}
                onChange={(e) => onChange("tier2Support", e.target.checked)}
                disabled={isConferenceReadOnly}
                className="h-4 w-4"
              />
              Tier 2
            </label>
            <span className={`${FORM_LINK_CLASS} text-sm mt-4`}>
              Refer to Tiered Support and Corrective Support Planning
            </span>
          </div>
        </div>
      </div>

      <FormActivityLog record={record} />
    </div>
  )
}

export default GoalPlanningForm

